/**
 * SPEC-Auth-Users-Roles-Audit — Admin Users management page.
 *
 * Renders the canonical `users` table. Display + inline edit on existing rows
 * (per Sarah's scope choice 2026-05-28): role dropdown of agent/senior/admin/bot
 * (bot is read-only), is_admin / dev checkboxes. (Slack IDs for red-dot tagging
 * live in Admin Settings → Red dot responders, not on the user row.)
 *
 * Provisioning new users (Joey/Tony/Elly/JZ) is out of scope for this SPEC's
 * UI surface — admins can POST /api/users directly. A follow-up SPEC can add
 * the create form.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRole } from "@/hooks/use-role";
import { useToast } from "@/hooks/use-toast";
import { Mail, Shield, Loader2, Save, Bot, UserCog, Code2 } from "lucide-react";
import { QATT_OPTIONS, toggleQatt, sameQatts } from "@shared/qatt-scope";

type Role = "agent" | "senior" | "admin" | "bot" | "trainee";

interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  isAdmin: boolean;
  dev: boolean;
  isActive: boolean;
  oidcSub: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  /** Per-user QATT scope. null = may check every task type. */
  allowedQatts: number[] | null;
}

interface RowEditState {
  role: Role;
  isAdmin: boolean;
  dev: boolean;
  /** null = unrestricted. Never an empty array (normalised to null). */
  allowedQatts: number[] | null;
}

// Which QA task types a user may check. All four unticked = no restriction.
// Only meaningful for Agents — the queue gate
// (server/lib/agent-qatt-scope-gate.ts) ignores it for every other role.
// Helpers live in shared/qatt-scope.ts so they are unit-tested.

function initialEdit(row: AdminUserRow): RowEditState {
  return {
    role: row.role,
    isAdmin: row.isAdmin,
    dev: row.dev,
    allowedQatts: row.allowedQatts ?? null,
  };
}

function isDirty(row: AdminUserRow, edit: RowEditState): boolean {
  return (
    edit.role !== row.role ||
    edit.isAdmin !== row.isAdmin ||
    edit.dev !== row.dev ||
    !sameQatts(edit.allowedQatts, row.allowedQatts)
  );
}

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "agent", label: "Agent" },
  { value: "senior", label: "Senior" },
  // SPEC-TrainingSimulator §3 — trainees reach /train only (guard lands in Phase B).
  { value: "trainee", label: "Trainee" },
  { value: "admin", label: "Admin" },
];

function roleBadgeColour(role: Role): "default" | "secondary" | "outline" {
  if (role === "admin") return "default";
  if (role === "senior") return "default";
  if (role === "bot") return "outline";
  return "secondary";
}

export default function AdminUsersPage() {
  const { isAdmin } = useRole();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [edits, setEdits] = useState<Record<string, RowEditState>>({});

  const { data: rows = [], isLoading } = useQuery<AdminUserRow[]>({
    queryKey: ["/api/users"],
    enabled: isAdmin,
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<RowEditState> }) => {
      const body: Record<string, unknown> = { ...patch };
      const res = await fetch(`/api/users/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as any)?.error ?? `Failed (${res.status})`);
      }
      return (await res.json()) as AdminUserRow;
    },
    onSuccess: (_updated, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[vars.id];
        return next;
      });
      toast({ title: "Saved", description: "User updated." });
    },
    onError: (err: any) => {
      toast({
        title: "Save failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  if (!isAdmin) {
    return (
      <div className="container max-w-4xl mx-auto py-6 px-4">
        <Card>
          <CardContent className="py-8 text-center">
            <Shield className="w-10 h-10 mx-auto mb-2 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
            <p className="text-sm text-muted-foreground">Only administrators can view this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getEdit = (row: AdminUserRow): RowEditState => edits[row.id] ?? initialEdit(row);

  const updateEdit = (row: AdminUserRow, patch: Partial<RowEditState>) => {
    setEdits((prev) => {
      const current = prev[row.id] ?? initialEdit(row);
      return { ...prev, [row.id]: { ...current, ...patch } };
    });
  };

  return (
    <div className="container max-w-7xl mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" data-testid="text-page-title">Users</h1>
        <p className="text-muted-foreground">
          Manage who can use the QAM and what they can do.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5" />
            Canonical users
          </CardTitle>
          <CardDescription>
            {rows.length} user{rows.length !== 1 ? "s" : ""} — agent / senior / admin / bot
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No users yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-center">is_admin</TableHead>
                  <TableHead className="text-center">dev</TableHead>
                  <TableHead title="Task types this Agent may check. None ticked = all types.">
                    Can check
                  </TableHead>
                  <TableHead className="text-right">Save</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const edit = getEdit(row);
                  const dirty = isDirty(row, edit);
                  const isBot = row.role === "bot";
                  return (
                    <TableRow key={row.id} data-testid={`row-user-${row.id}`}>
                      <TableCell className="font-medium" data-testid={`text-email-${row.id}`}>
                        {row.email}
                      </TableCell>
                      <TableCell data-testid={`text-name-${row.id}`}>{row.name}</TableCell>
                      <TableCell>
                        {isBot ? (
                          <Badge variant={roleBadgeColour(row.role)} className="flex items-center gap-1 w-fit">
                            <Bot className="h-3.5 w-3.5" />
                            bot
                          </Badge>
                        ) : (
                          <Select
                            value={edit.role}
                            onValueChange={(v) =>
                              // The task-type scope only applies to Agents and its
                              // boxes are hidden for other roles, so clear it when
                              // the role moves away — no invisible value left behind.
                              updateEdit(row, v === "agent"
                                ? { role: v as Role }
                                : { role: v as Role, allowedQatts: null })
                            }
                            data-testid={`select-role-${row.id}`}
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={edit.isAdmin}
                          disabled={isBot}
                          onCheckedChange={(v) => updateEdit(row, { isAdmin: v === true })}
                          data-testid={`checkbox-isAdmin-${row.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={edit.dev}
                          disabled={isBot}
                          onCheckedChange={(v) => updateEdit(row, { dev: v === true })}
                          data-testid={`checkbox-dev-${row.id}`}
                        />
                        {row.dev && <Code2 className="inline ml-1 h-3.5 w-3.5 text-muted-foreground" />}
                      </TableCell>
                      <TableCell>
                        {edit.role === "agent" ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {QATT_OPTIONS.map((opt) => (
                                <label
                                  key={opt.value}
                                  title={opt.title}
                                  className="flex items-center gap-1 text-xs cursor-pointer"
                                >
                                  <Checkbox
                                    checked={edit.allowedQatts?.includes(opt.value) ?? false}
                                    onCheckedChange={(v) =>
                                      updateEdit(row, {
                                        allowedQatts: toggleQatt(edit.allowedQatts, opt.value, v === true),
                                      })
                                    }
                                    data-testid={`checkbox-qatt-${opt.value}-${row.id}`}
                                  />
                                  {opt.label}
                                </label>
                              ))}
                            </div>
                            <span
                              className="text-[11px] text-muted-foreground"
                              data-testid={`text-qatt-scope-${row.id}`}
                            >
                              {edit.allowedQatts === null ? "All task types" : "Restricted"}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          disabled={!dirty || isBot || saveMutation.isPending}
                          onClick={() => saveMutation.mutate({ id: row.id, patch: edit })}
                          data-testid={`button-save-${row.id}`}
                        >
                          <Save className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
